import React, { useState } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Tag,
  Space,
  Select,
  Modal,
  message,
  Typography,
  Tooltip,
  Drawer,
  Spin,
  Descriptions,
} from 'antd';
import { useTable } from '@/hooks/useTable';
import {
  getActivityRuleList,
  deleteActivityRule,
  getActivityRuleDetail,
} from './api';
import type { ActivityRuleListParams, ActivityRule } from './types';
import dayjs from 'dayjs';
import ActivityRuleDrawer from './ActivityRuleDrawer';
import { ActivityRuleDetailContent } from './detail';
import { contestTypeMapColumn } from '@/pages/vote-activity/vote/type';
import styles from './ActivityRuleDrawer.module.less';

const { Text } = Typography;

const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '禁用', color: 'default' },
  1: { text: '已启用', color: 'processing' },
};

// 将 contestTypeMapColumn 转换为下拉框选项格式（用于搜索），value 使用 key（枚举值）
const ACTIVITY_TYPE_OPTIONS = Array.from(contestTypeMapColumn.entries()).map(
  ([key, label]) => ({
    label,
    value: key,
  })
);

const ActivityRulesPage: React.FC = () => {
  const [form] = Form.useForm();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ActivityRule>();
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [detailData, setDetailData] = useState<ActivityRule | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchParams, setSearchParams] = useState<ActivityRuleListParams>({
    currentPage: 1,
    pageSize: 10,
  });

  const {
    data,
    isLoading,
    handleTableChange,
    updateParams,
    currentPage,
    pageSize,
    total,
    refresh,
  } = useTable({
    queryKey: 'activity-rule-list',
    defaultPageSize: 10,
    queryFn: async (current, size, params) => {
      const response = await getActivityRuleList({
        ...params,
        currentPage: current,
        pageSize: size,
      });
      // 返回完整的分页数据对象
      return {
        records: response.data?.records || [],
        total: response.data?.total || 0,
        currentPage: response.data?.currentPage || current,
        pageSize: response.data?.pageSize || size,
      };
    },
    defaultParams: searchParams,
  });

  const handleSearch = async () => {
    const values = await form.validateFields();
    setSearchParams({ ...searchParams, ...values });
    updateParams({ ...searchParams, ...values });
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({
      currentPage: 1,
      pageSize: 10,
    });
    updateParams({
      currentPage: 1,
      pageSize: 10,
    });
  };

  const handleCreate = () => {
    setEditingRule(undefined);
    setIsDrawerOpen(true);
  };

  const handleEdit = async (record: ActivityRule) => {
    const res = await getActivityRuleDetail(record.id!);
    if (res.status !== 200) return;
    setEditingRule(res.data);
    setIsDrawerOpen(true);
  };

  const handleDelete = (record: ActivityRule) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除规则"${record.activityTopicJa || record.activityTopicEn}"吗？`,
      onOk: async () => {
        const res = await deleteActivityRule(record.id!);
        if (res.status === 200) {
          message.success('删除成功');
          refresh();
        } else {
          message.error(res.message || '删除失败');
        }
      },
    });
  };

  const handleViewDetail = async (record: ActivityRule) => {
    setIsDetailDrawerOpen(true);
    setDetailLoading(true);
    try {
      const res = await getActivityRuleDetail(record.id!);
      if (res.status === 200 && res.data) {
        setDetailData(res.data);
      } else {
        message.error(res.message || '获取详情失败');
        setIsDetailDrawerOpen(false);
      }
    } catch (error) {
      message.error('获取详情失败');
      setIsDetailDrawerOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // const handleViewDetail = (id: number) => {
  //   // 详情页面可以通过点击表格行或添加详情按钮来访问
  //   // 这里暂时不实现，因为图片中没有显示详情按钮
  // };

  const handleDrawerSuccess = () => {
    refresh();
    setIsDrawerOpen(false);
    setEditingRule(undefined);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setEditingRule(undefined);
  };

  const columns = [
    {
      title: 'RuleId',
      dataIndex: 'id',
      // width: 40,
    },
    {
      title: '活动类型',
      dataIndex: 'activityType',
      // width: 40,
      render: (activityType: number) => {
        if (!activityType) return '-';
        // 使用 contestTypeMapColumn 将 key 转换为文本显示
        return contestTypeMapColumn.get(activityType) || '-';
      },
    },
    {
      title: '日期',
      key: 'date',
      // width: 80,
      render: (_: any, record: ActivityRule) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <div>
            开始：
            {record.startTime
              ? dayjs(record.startTime).format('YYYY-MM-DD HH:mm')
              : '-'}
          </div>
          <div>
            结束：
            {record.endTime
              ? dayjs(record.endTime).format('YYYY-MM-DD HH:mm')
              : '-'}
          </div>
        </div>
      ),
    },
    {
      title: '主题',
      key: 'theme',
      // width: 80,
      render: (_: any, record: ActivityRule) => (
        <div>
          <div>{record.activityTopicJa}</div>
          <div>{record.activityTopicEn}</div>
        </div>
      ),
    },
    {
      title: '奖品设置',
      key: 'rewardSettings',
      // width: 120,
      render: (_: any, record: ActivityRule) => (
        <Tooltip
          placement="right"
          mouseEnterDelay={0.3}
          mouseLeaveDelay={0.1}
          overlayInnerStyle={{ maxWidth: '500px', minWidth: '400px' }}
          title={record.rewardSettings?.map((item, index) => (
            <div key={index} style={{ marginBottom: 4 }}>
              <div>{item.descriptionJa}</div>
              <div>{item.descriptionEn}</div>
            </div>
          ))}
        >
          <div style={{ width: '100%' }}>
            {record.rewardSettings?.map((item, index) => (
              <div key={index} style={{ marginBottom: 4 }} className={styles.textOverFlow}>
                <div>{item.descriptionJa}</div>
                <div>{item.descriptionEn}</div>
              </div>
            ))}
          </div>
        </Tooltip>
      ),
    },
    {
      title: '描述',
      key: 'description',
      // width: 120,
      render: (_: any, record: ActivityRule) => (
        <Tooltip
          placement="topLeft"
          title={
            <div>
              <div>{record.descriptionJa}</div>
              <div>{record.descriptionEn}</div>
            </div>
          }
        >
          <div className={styles.textOverFlow}>
            <div>{record.descriptionJa}</div>
            <div>{record.descriptionEn}</div>
          </div>
        </Tooltip>
      ),
    },

    // {
    //   title: '参赛作品要求',
    //   key: 'submissionRequirements',
    //   width: 150,
    //   render: (_: any, record: ActivityRule) => (
    //     <div>
    //       {record.submissionRequirements?.map((item, index) => (
    //         <div key={index}>● {item.requirementJa || item.requirementEn}</div>
    //       ))}
    //     </div>
    //   ),
    // },
    // {
    //   title: '获奖作品评判标准',
    //   key: 'awardCriteria',
    //   width: 150,
    //   render: (_: any, record: ActivityRule) => (
    //     <div>
    //       {record.awardCriteria?.map((item, index) => (
    //         <div key={index}>● {item.criterionJa || item.criterionEn}</div>
    //       ))}
    //     </div>
    //   ),
    // },
    // {
    //   title: '常见问题',
    //   key: 'commonQuestions',
    //   width: 150,
    //   render: (_: any, record: ActivityRule) => (
    //     <div>
    //       {record.commonQuestions?.map((item, index) => (
    //         <div key={index}>● {item.titleJa || item.titleEn}</div>
    //       ))}
    //     </div>
    //   ),
    // },
    // {
    //   title: '重要提示',
    //   key: 'importantNotes',
    //   width: 150,
    //   render: (_: any, record: ActivityRule) => (
    //     <div>
    //       {record.importantNotes?.map((item, index) => (
    //         <div key={index}>● {item.noteJa || item.noteEn}</div>
    //       ))}
    //     </div>
    //   ),
    // },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      // width: 100,
      render: (status: number) => (
        <Tag color={STATUS_MAP[status]?.color}>{STATUS_MAP[status]?.text}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      // width: 100,
      fixed: 'right' as const,
      render: (_: any, record: ActivityRule) => (
        <Space>
          {/* <Button type="link" onClick={() => handleViewDetail(record)}>
            查看详情
          </Button> */}
          <Button type="link" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="活动规则管理"
      extra={
        <Button type="primary" onClick={handleCreate}>
          新建规则
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="id">
            <Input placeholder="请输入RuleId(活动ID)" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="activityType">
            <Select
              style={{ width: 160 }}
              placeholder="请选择活动类型"
              allowClear
              options={ACTIVITY_TYPE_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="updateUserName">
            <Input placeholder="请输入更新人" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="status">
            <Select
              style={{ width: 160 }}
              placeholder="请选择状态"
              allowClear
              options={Object.entries(STATUS_MAP).map(([value, { text }]) => ({
                label: text,
                value: Number(value),
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        loading={isLoading}
        onChange={handleTableChange}
        // scroll={{ x: 2000 }}
      />

      {isDrawerOpen && (
        <ActivityRuleDrawer
          visible={isDrawerOpen}
          editData={editingRule}
          onSuccess={handleDrawerSuccess}
          onClose={handleDrawerClose}
        />
      )}

      <Drawer
        title="活动规则详情"
        open={isDetailDrawerOpen}
        onClose={() => {
          setIsDetailDrawerOpen(false);
          setDetailData(null);
        }}
        width={800}
        destroyOnClose
      >
        <Spin spinning={detailLoading}>
          {detailData && <ActivityRuleDetailContent detail={detailData} />}
        </Spin>
      </Drawer>
    </Card>
  );
};

export default ActivityRulesPage;
